import * as THREE from "three";
import { PLANOGRAM_SCALE } from "../constants";
import { Color } from "src/elements";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { Font, FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import { create3dCamera, createFrontCamera } from "./cameras";

let loadedThreeFont: Font | undefined = undefined;
const textureCache: Record<string, THREE.Texture | null> = {};

export const loadThreeFont = () => {
  if (loadedThreeFont) {
    return Promise.resolve(loadedThreeFont);
  }

  const loader = new FontLoader();
  new Promise((resolve, reject) => {
    loader.load(
      "/fonts/helvetiker_regular.typeface.json",
      (font) => {
        loadedThreeFont = font;
        resolve(font);
      },
      undefined,
      (error) => reject(error),
    );
  });
};

const loadTexture = (mesh: THREE.Mesh, textureUrl: string) => {
  if (textureCache[textureUrl] === undefined) {
    const loader = new THREE.TextureLoader();
    loader.load(
      textureUrl,

      // On successful texture load.
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        textureCache[textureUrl] = texture;
        applyTexture(mesh, texture);
      },

      // On progress.
      undefined,

      // On error.
      () => {
        textureCache[textureUrl] = null;
      },
    );
  } else {
    if (textureCache[textureUrl]) {
      applyTexture(mesh, textureCache[textureUrl]);
    }
  }
};

const applyTexture = (mesh: THREE.Mesh, texture: THREE.Texture) => {
  // Old material cleanup.
  if (Array.isArray(mesh.material)) {
    mesh.material.forEach((material) => material.dispose());
  } else {
    mesh.material.dispose();
  }

  const basicMaterial = new THREE.MeshBasicMaterial({
    map: texture,
  });

  mesh.material = basicMaterial;
  mesh.material.needsUpdate = true;
};

const disposeTexture = (textureUrl?: string) => {
  if (textureUrl) {
    const texture = textureCache[textureUrl];
    if (texture) {
      texture.dispose();
    }

    delete textureCache[textureUrl];
  }
};

export const initializeScene = (
  container: HTMLDivElement,
  cameraStyle: "FRONT" | "3D",
  pog: Pog,
) => {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(Color.sand50);

  const ambientLight = new THREE.AmbientLight(Color.white, 0.6);
  const directionalLight = new THREE.DirectionalLight(Color.white, 0.8);
  directionalLight.position.set(0, 0, 1);
  scene.add(ambientLight, directionalLight);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(container.clientWidth, container.clientHeight);

  if (container.children.length > 0) {
    container.replaceChild(renderer.domElement, container.children[0]);
  } else {
    container.appendChild(renderer.domElement);
  }

  const { camera, cleanup } =
    cameraStyle === "FRONT"
      ? createFrontCamera(container, pog)
      : create3dCamera(renderer, container, pog);

  return { scene, renderer, camera, cleanup };
};

export const animate = (
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: () => THREE.Camera,
  movementUpdater?: () => void,
) => {
  let frameId: number;

  const animateFrame = () => {
    frameId = requestAnimationFrame(animateFrame);
    if (movementUpdater) movementUpdater();
    renderer.render(scene, camera());
  };

  animateFrame();

  return () => cancelAnimationFrame(frameId);
};

export const createThreeMesh = (options: {
  scene: THREE.Scene;
  width: number;
  height: number;
  depth: number;
  x: number;
  y: number;
  z: number;
  color: string;
  opacity?: number;
  renderOrder?: number;
  label?: string;
  textureUrl?: string;
}) => {
  const {
    scene,
    width,
    height,
    depth,
    x,
    y,
    z,
    color,
    opacity = 1,
    renderOrder = 1,
    label,
    textureUrl,
  } = options;

  const scaledWidth = width * PLANOGRAM_SCALE;
  const scaledHeight = height * PLANOGRAM_SCALE;
  const scaledDepth = depth * PLANOGRAM_SCALE;

  const geometry = new THREE.BoxGeometry(scaledWidth, scaledHeight, scaledDepth);

  // Default color-only standard material
  const standardMaterial = new THREE.MeshStandardMaterial({
    color,
    opacity,
    transparent: opacity < 1,
  });

  // Mesh typed with flexible material type to allow swapping materials
  const mesh = new THREE.Mesh<THREE.BoxGeometry, THREE.Material>(geometry, standardMaterial);

  mesh.renderOrder = renderOrder;

  mesh.position.set(
    x * PLANOGRAM_SCALE + scaledWidth / 2,
    y * PLANOGRAM_SCALE + scaledHeight / 2,
    z * PLANOGRAM_SCALE + scaledDepth / 2,
  );

  scene.add(mesh);

  // Mesh texture loader
  if (textureUrl) {
    loadTexture(mesh, textureUrl);
  }

  // Label logic
  let labelMesh: THREE.Mesh | undefined;

  if (label && loadedThreeFont) {
    const textSize = Math.min(scaledWidth, scaledHeight) * 0.2;

    const textGeometry = new TextGeometry(label, {
      font: loadedThreeFont,
      size: textSize,
      depth: 0.1,
      curveSegments: 4,
      bevelEnabled: false,
    });

    textGeometry.computeBoundingBox();

    if (textGeometry.boundingBox) {
      const { boundingBox } = textGeometry;
      const textWidth = boundingBox.max.x - boundingBox.min.x;
      const textHeight = boundingBox.max.y - boundingBox.min.y;

      const textMaterial = new THREE.MeshBasicMaterial({
        color: Color.white,
      });

      labelMesh = new THREE.Mesh(textGeometry, textMaterial);

      labelMesh.position.set(
        mesh.position.x - textWidth / 2,
        mesh.position.y - textHeight / 2,
        mesh.position.z + scaledDepth / 2 + 0.01,
      );

      scene.add(labelMesh);
    }
  }

  return {
    mesh,
    cleanUp: () => {
      scene.remove(mesh);
      geometry.dispose();

      disposeTexture(textureUrl);

      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((material) => material.dispose());
      } else {
        mesh.material.dispose();
      }

      if (labelMesh) {
        scene.remove(labelMesh);
        labelMesh.geometry.dispose();
        if (Array.isArray(labelMesh.material)) {
          labelMesh.material.forEach((material) => material.dispose());
        } else {
          labelMesh.material.dispose();
        }
      }
    },
  };
};
